import React, { useEffect, useState, useCallback } from 'react';
import {
    Paper, Typography, Box, Skeleton, useTheme, alpha,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Rating, Tooltip, Button, Chip
} from '@mui/material';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import ScienceIcon from '@mui/icons-material/Science';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import axios from 'axios';
import Cookies from '../../common/Cookies';
import { formatDateForDisplay } from '../../utils/timezoneUtils';

export default function RecentChatsCard() {
    const [chats, setChats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const theme = useTheme();

    const fetchRecentChats = useCallback(async () => {
        try {
            setLoading(true);
            const cookieToken = Cookies.getJwtToken();
            const lsToken = localStorage.getItem('jwtToken');
            const availableToken = cookieToken || lsToken;
            if (availableToken && !axios.defaults.headers.common['Authorization']) {
                axios.defaults.headers.common['Authorization'] = `Bearer ${availableToken}`;
            }

            const resp = await axios.get('/dashboard/recentChats');
            const parsed = (resp.data || []).map(chat => {
                if (chat.chat_data && typeof chat.chat_data === 'string') {
                    try { chat.chat_data = JSON.parse(chat.chat_data); } catch {}
                }
                return chat;
            });
            setChats(parsed.slice(0, 5));
            setError(null);
        } catch {
            setError('Unable to load recent chats');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchRecentChats(); }, [fetchRecentChats]);

    const msgCount = (chat) =>
        chat.chat_data?.messages ? chat.chat_data.messages.length : 0;

    return (
        <Paper elevation={0} sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
            transition: 'box-shadow 0.2s ease',
            '&:hover': { boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}` },
        }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2.5,
                pb: 1.5,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                    }}>
                        <ForumRoundedIcon />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Recent Conversations
                    </Typography>
                </Box>
                <Button
                    size="small"
                    endIcon={<OpenInNewRoundedIcon sx={{ fontSize: 14 }} />}
                    onClick={() => window.location.assign('/#/analytics')}
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
                >
                    View All
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ px: 2.5, pb: 2.5 }}>
                    {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} variant="rounded" height={40} sx={{ mb: 1, borderRadius: 1.5 }} />
                    ))}
                </Box>
            ) : error ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {error}
                    </Typography>
                </Box>
            ) : chats.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        No recent conversations yet
                    </Typography>
                </Box>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: `1px solid ${theme.palette.divider}` }}>
                                    Date
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: `1px solid ${theme.palette.divider}` }}>
                                    Agent
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: `1px solid ${theme.palette.divider}` }}>
                                    Messages
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', borderBottom: `1px solid ${theme.palette.divider}` }}>
                                    Rating
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {chats.map((chat, idx) => {
                                const hasRating = chat.chat_rating != null;
                                return (
                                    <TableRow key={idx} sx={{
                                        '&:last-child td': { borderBottom: 0 },
                                        '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.04) },
                                    }}>
                                        <TableCell sx={{ fontSize: '0.8rem', py: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {chat.internal && (
                                                    <Tooltip title="Test Chat">
                                                        <ScienceIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                                                    </Tooltip>
                                                )}
                                                {formatDateForDisplay(chat.date_created)}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.8rem', py: 1.5, maxWidth: 180 }}>
                                            <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem' }}>
                                                {chat.agent_name || chat.agent_id || 'Unknown'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center" sx={{ py: 1.5 }}>
                                            <Chip
                                                label={msgCount(chat)}
                                                size="small"
                                                sx={{
                                                    height: 22,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 700,
                                                    bgcolor: alpha(theme.palette.info.main, 0.08),
                                                    color: theme.palette.info.main,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="center" sx={{ py: 1.5 }}>
                                            {hasRating ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                    <Rating value={chat.chat_rating} readOnly max={5} size="small" sx={{ fontSize: 14 }} />
                                                </Box>
                                            ) : (
                                                <Typography variant="caption" color="text.disabled">--</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    );
}
